import { useEffect, useState, useCallback } from 'react';
import { Card, Grid, Avatar, List, Empty, Tag, Dialog, Popup, Toast, Button } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../hooks/useTransactions';
import { useStore } from '../store';
import { formatCurrency } from '@tangtung/shared';
import {
    CalculatorOutline,
    HistogramOutline,
    PayCircleOutline,
    SetOutline
} from 'antd-mobile-icons';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar, Send, TrendingUp } from 'lucide-react';
import './HomePage.css';

// Thai month names
const THAI_MONTHS = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const THAI_DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

type FilterType = 'today' | '7days' | '30days' | 'custom';

export default function HomePage() {
    const user = useStore((s) => s.user);
    const navigate = useNavigate();
    const { transactions, refreshTransactions } = useTransactions();

    // State from SummaryPage
    const [filterType, setFilterType] = useState<FilterType>('today');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date());
    const [selectedTx, setSelectedTx] = useState<typeof transactions[0] | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [showCompare, setShowCompare] = useState(false);

    useEffect(() => {
        refreshTransactions();
    }, [refreshTransactions]);

    const quickActions = [
        { icon: <CalculatorOutline />, label: 'คิดเงิน', path: '/calculator', color: '#4facfe' },
        { icon: <HistogramOutline />, label: 'สลิป', path: '/slips', color: '#43e97b' }, // Changed to Slips
        { icon: <PayCircleOutline />, label: 'สร้าง QR', path: '/qr', color: '#fa709a' },
        { icon: <SetOutline />, label: 'ตั้งค่า', path: '/profile', color: '#a18cd1' },
    ];

    // --- Logic from SummaryPage ---

    // Send report to LINE
    const handleSendReport = async () => {
        if (!user?.userId) {
            Toast.show({ icon: 'fail', content: 'กรุณาเข้าสู่ระบบก่อน' });
            return;
        }

        setIsSending(true);
        try {
            const response = await fetch('https://tangtungdev-api.greenslime2009.workers.dev/api/chatbot/send-report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line_user_id: user.userId,
                    filter_type: filterType,
                }),
            });

            if (response.ok) {
                Toast.show({ icon: 'success', content: 'ส่งรายงานเข้า LINE แล้วค่ะ!' });
            } else {
                Toast.show({ icon: 'fail', content: 'ส่งรายงานไม่สำเร็จ' });
            }
        } catch {
            Toast.show({ icon: 'fail', content: 'เกิดข้อผิดพลาด' });
        } finally {
            setIsSending(false);
        }
    };

    // Send comparison to LINE
    const handleSendComparison = async () => {
        if (!user?.userId) {
            Toast.show({ icon: 'fail', content: 'กรุณาเข้าสู่ระบบก่อน' });
            return;
        }

        setIsSending(true);
        try {
            const response = await fetch('https://tangtungdev-api.greenslime2009.workers.dev/api/chatbot/send-comparison', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line_user_id: user.userId,
                }),
            });

            if (response.ok) {
                Toast.show({ icon: 'success', content: 'ส่งรายงานเปรียบเทียบเข้า LINE แล้วค่ะ!' });
                setShowCompare(false);
            } else {
                Toast.show({ icon: 'fail', content: 'ส่งรายงานไม่สำเร็จ' });
            }
        } catch {
            Toast.show({ icon: 'fail', content: 'เกิดข้อผิดพลาด' });
        } finally {
            setIsSending(false);
        }
    };

    // Helper to parse UTC date from API
    const parseDate = (dateStr: string) => {
        return new Date(dateStr.replace(' ', 'T') + 'Z');
    };

    // Get date range based on filter type
    const getDateRange = (type: FilterType): { start: Date; end: Date } => {
        if (type === 'custom' && selectedDate) {
            const start = new Date(selectedDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(selectedDate);
            end.setHours(23, 59, 59, 999);
            return { start, end };
        }

        const now = new Date();
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);

        const start = new Date(now);
        start.setHours(0, 0, 0, 0);

        switch (type) {
            case 'today':
                break;
            case '7days':
                start.setDate(start.getDate() - 6);
                break;
            case '30days':
                start.setDate(start.getDate() - 29);
                break;
        }

        return { start, end };
    };

    // Get stats for filter range
    const getFilterStats = useCallback((type: FilterType) => {
        const { start, end } = getDateRange(type);
        const startStr = start.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
        const endStr = end.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

        const filteredTx = transactions.filter((tx) => {
            const utcDateStr = tx.created_at.replace(' ', 'T') + 'Z';
            const txDate = new Date(utcDateStr);
            const txDateStr = txDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
            return txDateStr >= startStr && txDateStr <= endStr;
        });

        const cashTx = filteredTx.filter((tx) => tx.payment_type === 'cash');
        const qrTx = filteredTx.filter((tx) => tx.payment_type === 'qr');

        return {
            total: filteredTx.reduce((sum, tx) => sum + tx.amount, 0),
            count: filteredTx.length,
            cash: cashTx.reduce((sum, tx) => sum + tx.amount, 0),
            qr: qrTx.reduce((sum, tx) => sum + tx.amount, 0),
            transactions: filteredTx,
        };
    }, [transactions]);

    const filterStats = getFilterStats(filterType);

    // Get filtered transactions for list display
    const getFilteredTransactions = useCallback(() => {
        const { start, end } = getDateRange(filterType);
        const startStr = start.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
        const endStr = end.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

        return transactions.filter((tx) => {
            const utcDateStr = tx.created_at.replace(' ', 'T') + 'Z';
            const txDate = new Date(utcDateStr);
            const txDateStr = txDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
            return txDateStr >= startStr && txDateStr <= endStr;
        });
    }, [transactions, filterType]);

    const filteredTransactions = getFilteredTransactions();

    // Group filtered transactions by date
    const groupedTransactions = filteredTransactions.reduce((groups, tx) => {
        const txDate = parseDate(tx.created_at);
        const date = txDate.toLocaleDateString('th-TH', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Bangkok',
        });
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(tx);
        return groups;
    }, {} as Record<string, typeof transactions>);

    // Format filter label
    const getFilterLabel = (type: FilterType) => {
        switch (type) {
            case 'today': return 'วันนี้';
            case '7days': return '7 วัน';
            case '30days': return '30 วัน';
            case 'custom':
                if (selectedDate) {
                    return selectedDate.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
                }
                return 'เลือกวัน';
        }
    };

    // Quick select handlers
    const selectFilter = (type: FilterType) => {
        setFilterType(type);
        setSelectedDate(null);
        setShowDatePicker(false);
    };

    // Select a specific date from calendar
    const selectDate = (date: Date) => {
        setSelectedDate(date);
        setFilterType('custom');
        setShowDatePicker(false);
    };

    // Calendar helpers
    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 1).getDay();
    };

    const prevMonth = () => {
        setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1);
        if (next <= new Date()) {
            setCalendarMonth(next);
        }
    };

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.toLocaleDateString('en-CA') === d2.toLocaleDateString('en-CA');
    };

    const renderCalendar = () => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const today = new Date();

        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const isTodayDate = isSameDay(date, today);
            const isFuture = date > today;
            const isSelected = selectedDate && isSameDay(date, selectedDate);

            days.push(
                <button
                    key={day}
                    className={`calendar-day ${isTodayDate ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                    disabled={isFuture}
                    onClick={() => !isFuture && selectDate(date)}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    return (
        <div className="page home-page">
            <div className="page-content">
                {/* User Header */}
                <div className="home-header">
                    <div className="greeting">
                        <h1>สวัสดี {user?.displayName?.split(' ')[0] || 'คุณ'} 👋</h1>
                        <p>วันนี้ยอดขายเป็นยังไงบ้าง?</p>
                    </div>
                    <Avatar
                        src={user?.pictureUrl || ''}
                        style={{ '--size': '48px', '--border-radius': '24px' }}
                    />
                </div>

                {/* Stats Card (From SummaryPage) */}
                <Card className="summary-stats-card">
                    <div className="summary-header">
                        <h2>สรุปยอดขาย</h2>
                        <button
                            className="date-picker-btn"
                            onClick={() => setShowDatePicker(true)}
                        >
                            <Calendar size={14} />
                            <span>{getFilterLabel(filterType)}</span>
                            <ChevronDown size={14} />
                        </button>
                    </div>
                    <div className="summary-total">{formatCurrency(filterStats.total)}</div>
                    <div className="summary-breakdown">
                        <div className="breakdown-item">
                            <span className="breakdown-label">💵 เงินสด</span>
                            <span className="breakdown-value">{formatCurrency(filterStats.cash)}</span>
                        </div>
                        <div className="breakdown-item">
                            <span className="breakdown-label">📱 QR</span>
                            <span className="breakdown-value">{formatCurrency(filterStats.qr)}</span>
                        </div>
                        <div className="breakdown-item">
                            <span className="breakdown-label">📄 บิล</span>
                            <span className="breakdown-value">{filterStats.count} รายการ</span>
                        </div>
                    </div>
                </Card>

                {/* Action Buttons */}
                <div className="action-buttons-home">
                    <span className="compare-link" onClick={() => setShowCompare(true)}>
                        <TrendingUp size={14} /> เปรียบเทียบยอด
                    </span>
                </div>

                {/* Quick Actions (Rich Menu) */}
                <div className="quick-actions">
                    <Grid columns={4} gap={12}>
                        {quickActions.map((action) => (
                            <Grid.Item key={action.path}>
                                <div className="action-item" onClick={() => navigate(action.path)}>
                                    <div className="action-icon-circle" style={{ background: action.color }}>
                                        {action.icon}
                                    </div>
                                    <span className="action-label">{action.label}</span>
                                </div>
                            </Grid.Item>
                        ))}
                    </Grid>
                </div>

                {/* Transaction List */}
                <div className="transactions-section">
                    <h3>รายการทั้งหมด</h3>
                    {filteredTransactions.length === 0 ? (
                        <Empty description="ยังไม่มีรายการ" className="empty-home" />
                    ) : (
                        Object.entries(groupedTransactions).map(([date, txs]) => (
                            <List key={date} header={date} className="transaction-list">
                                {txs.map((tx) => (
                                    <List.Item
                                        key={tx.id}
                                        onClick={() => setSelectedTx(tx)}
                                        extra={
                                            <span className={`tx-amount ${tx.payment_type}`}>
                                                {formatCurrency(tx.amount)}
                                            </span>
                                        }
                                        description={parseDate(tx.created_at).toLocaleTimeString('th-TH', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'Asia/Bangkok',
                                        })}
                                        arrow
                                    >
                                        <Tag
                                            color={tx.payment_type === 'cash' ? 'success' : 'primary'}
                                            fill="outline"
                                        >
                                            {tx.payment_type === 'cash' ? '💵 เงินสด' : '📱 QR'}
                                        </Tag>
                                        {tx.slip_url && <span style={{ marginLeft: 8 }}>📷</span>}
                                    </List.Item>
                                ))}
                            </List>
                        ))
                    )}
                </div>

                <div className="spacer-bottom"></div>
            </div>

            {/* Date Picker Popup */}
            <Popup
                visible={showDatePicker}
                onMaskClick={() => setShowDatePicker(false)}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
            >
                <div className="date-picker-modal">
                    <div className="date-picker-header">
                        <h3>เลือกวันที่</h3>
                        <button className="close-btn" onClick={() => setShowDatePicker(false)}>✕</button>
                    </div>

                    <div className="quick-select">
                        <button className={`quick-btn ${filterType === 'today' ? 'active' : ''}`} onClick={() => selectFilter('today')}>วันนี้</button>
                        <button className={`quick-btn ${filterType === '7days' ? 'active' : ''}`} onClick={() => selectFilter('7days')}>7 วัน</button>
                        <button className={`quick-btn ${filterType === '30days' ? 'active' : ''}`} onClick={() => selectFilter('30days')}>30 วัน</button>
                    </div>

                    <div className="mini-calendar">
                        <div className="calendar-header">
                            <button className="nav-btn" onClick={prevMonth}><ChevronLeft size={20} /></button>
                            <span className="month-year">{THAI_MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear() + 543}</span>
                            <button className="nav-btn" onClick={nextMonth}><ChevronRight size={20} /></button>
                        </div>
                        <div className="calendar-weekdays">
                            {THAI_DAYS.map((day) => (<div key={day} className="weekday">{day}</div>))}
                        </div>
                        <div className="calendar-days">
                            {renderCalendar()}
                        </div>
                    </div>
                </div>
            </Popup>

            {/* Comparison Popup */}
            <Popup
                visible={showCompare}
                onMaskClick={() => setShowCompare(false)}
                position="bottom"
                bodyStyle={{ borderRadius: '16px 16px 0 0' }}
            >
                <div className="compare-popup">
                    <div className="compare-header">
                        <TrendingUp size={24} />
                        <h3>เปรียบเทียบยอดขาย</h3>
                    </div>
                    <p className="compare-desc">
                        ส่งรายงานเปรียบเทียบยอดขาย <strong>เดือนนี้ vs เดือนก่อน</strong> เข้า LINE
                    </p>
                    <div className="compare-actions">
                        <Button
                            className="compare-send-btn"
                            color="primary"
                            loading={isSending}
                            onClick={handleSendComparison}
                        >
                            <Send size={16} />
                            ส่งรายงานเปรียบเทียบ
                        </Button>
                        <Button
                            className="compare-cancel-btn"
                            onClick={() => setShowCompare(false)}
                        >
                            ยกเลิก
                        </Button>
                    </div>
                </div>
            </Popup>

            {/* Transaction Detail Modal */}
            <Dialog
                visible={!!selectedTx}
                onClose={() => setSelectedTx(null)}
                closeOnMaskClick={true}
                title="รายละเอียดรายการ"
                content={
                    selectedTx && (
                        <div className="tx-detail-modal">
                            <div className="tx-detail-amount">{formatCurrency(selectedTx.amount)}</div>
                            <div className="tx-detail-row">
                                <span className="label">วันที่:</span>
                                <span>{parseDate(selectedTx.created_at).toLocaleDateString('th-TH', {
                                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                                    timeZone: 'Asia/Bangkok',
                                })}</span>
                            </div>
                            <div className="tx-detail-row">
                                <span className="label">ประเภท:</span>
                                <span>{selectedTx.payment_type === 'cash' ? 'เงินสด' : 'QR PromptPay'}</span>
                            </div>
                            {selectedTx.slip_url && (
                                <div className="tx-detail-slip">
                                    <div className="slip-label">หลักฐานการชำระเงิน (สลิป)</div>
                                    <img src={selectedTx.slip_url} alt="Slip" className="slip-image" />
                                </div>
                            )}
                        </div>
                    )
                }
                actions={[{ key: 'close', text: 'ปิด', onClick: () => setSelectedTx(null) }]}
            />
        </div>
    );
}
